import { useEffect, useRef, useState } from 'react'
import { Check, Columns, Sliders, Sparkles } from 'lucide-react'
import {
  CRITERIA_LABELS,
  SCORING_FIELD_KEYS,
  type ScoringFieldKey,
  type Weights,
} from '@ispot/shared'
import { WeightsPanel } from './WeightsPanel'

interface Props {
  weights: Weights
  dirty: boolean
  visibleCriteria: Set<ScoringFieldKey>
  aiRunning: boolean
  canGenerate: boolean
  onWeightChange: (key: ScoringFieldKey, value: number) => void
  onWeightCommit: () => void
  onWeightReset: () => void
  onToggleCriterion: (key: ScoringFieldKey) => void
  onGenerateAll: () => void
}

export function OverviewToolbar({
  weights,
  dirty,
  visibleCriteria,
  aiRunning,
  canGenerate,
  onWeightChange,
  onWeightCommit,
  onWeightReset,
  onToggleCriterion,
  onGenerateAll,
}: Props) {
  return (
    <div className="flex items-center justify-end gap-2">
      <ColumnsPopover
        visibleCriteria={visibleCriteria}
        onToggle={onToggleCriterion}
      />
      <WeightsPopover
        weights={weights}
        dirty={dirty}
        onChange={onWeightChange}
        onCommit={onWeightCommit}
        onReset={onWeightReset}
      />
      <button
        type="button"
        onClick={onGenerateAll}
        disabled={!canGenerate || aiRunning}
        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-market)' }}
        title="Score every segment with AI (sparse-fill preserves manual edits)"
      >
        <Sparkles className="h-3 w-3" />
        {aiRunning ? 'Scoring…' : 'Score all with AI'}
      </button>
    </div>
  )
}

// ============================================================================
// COLUMNS POPOVER — toggle which criteria show in the heatmap
// ============================================================================

function ColumnsPopover({
  visibleCriteria,
  onToggle,
}: {
  visibleCriteria: Set<ScoringFieldKey>
  onToggle: (key: ScoringFieldKey) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false))
  const hiddenCount = SCORING_FIELD_KEYS.length - visibleCriteria.size
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-[var(--color-warm-100)]"
        style={{
          borderColor: 'var(--color-border-subtle)',
          color: 'var(--color-text)',
        }}
      >
        <Columns className="h-3 w-3" />
        Columns
        {hiddenCount > 0 ? (
          <span
            className="rounded-full px-1.5 text-[10px] font-semibold"
            style={{
              backgroundColor: 'var(--color-warm-200)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {visibleCriteria.size}/{SCORING_FIELD_KEYS.length}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-20 mt-1 w-[240px] rounded-md border p-2 shadow-md"
          style={{
            backgroundColor: 'var(--color-bg, white)',
            borderColor: 'var(--color-border-subtle)',
          }}
        >
          <div
            className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Visible criteria
          </div>
          {SCORING_FIELD_KEYS.map((key) => {
            const shown = visibleCriteria.has(key)
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggle(key)}
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-[var(--color-warm-50)]"
                style={{ color: 'var(--color-text)' }}
              >
                <span>{CRITERIA_LABELS[key].label}</span>
                {shown ? (
                  <Check
                    className="h-3.5 w-3.5"
                    style={{ color: 'var(--color-market)' }}
                  />
                ) : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

// ============================================================================
// WEIGHTS POPOVER
// ============================================================================

function WeightsPopover({
  weights,
  dirty,
  onChange,
  onCommit,
  onReset,
}: {
  weights: Weights
  dirty: boolean
  onChange: (key: ScoringFieldKey, value: number) => void
  onCommit: () => void
  onReset: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useClickOutside<HTMLDivElement>(() => {
    if (dirty) onCommit()
    setOpen(false)
  })
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-[var(--color-warm-100)]"
        style={{
          borderColor: dirty ? 'var(--color-market)' : 'var(--color-border-subtle)',
          color: 'var(--color-text)',
        }}
      >
        <Sliders className="h-3 w-3" />
        Weights
        {dirty ? (
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: 'var(--color-market)' }}
            aria-label="unsaved changes"
          />
        ) : null}
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-20 mt-1 w-[340px] rounded-md border p-3 shadow-md"
          style={{
            backgroundColor: 'var(--color-bg, white)',
            borderColor: 'var(--color-border-subtle)',
          }}
        >
          <WeightsPanel
            weights={weights}
            dirty={dirty}
            onChange={onChange}
            onCommit={onCommit}
            onReset={onReset}
          />
        </div>
      ) : null}
    </div>
  )
}

// ============================================================================
// CLICK-OUTSIDE HOOK
// ============================================================================

function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onOutside])
  return ref
}
